import type { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';

import { BaseStore } from '@cartwhl/common/stores/BaseStore';
import { createStoreProvider } from '@cartwhl/common/stores/createStoreProvider';

import * as globalStores from '../../../stores/global';
import {
  canRedoAction as canRedoStackAction,
  canUndoAction as canUndoStackAction,
} from '../actionStack';
import { canUseMotionEditorRepathing } from '../feature-flags';
import {
  type MotionEditorStore,
  Context as MotionEditorStoreContext,
} from '../motionEditorStore';
import {
  REPATHING_BRUSH_RADIUS_MAX,
  REPATHING_BRUSH_RADIUS_MIN,
  REPATHING_BRUSH_STRENGTH_MAX,
  REPATHING_BRUSH_STRENGTH_MIN,
  REPATHING_DEFAULT_BRUSH_RADIUS,
  REPATHING_DEFAULT_BRUSH_STRENGTH,
} from './constants';
import {
  appendRepathingActionEntryInPlace,
  areRepathingActionSnapshotsEqual,
  cloneRepathingActionSnapshot,
  createInitialRepathingActionStackState,
  createRepathingActionEntry,
} from './repathingActionStack';
import {
  applyRepathingBrush,
  buildRepathingRoot2dConstraint,
  cloneRepathingCurve,
  createRepathingCurveFromBvh,
  moveRepathingHandle,
  moveRepathingPoint,
  simplifyRepathingCurve,
  smoothRepathingCurve,
} from './repathingCurve';
import {
  type RegenerateSceneObjectMotionPathInput,
  regenerateSceneObjectMotionPath,
} from './repathingGenerationActions';
import type {
  MotionEditControlsMode,
  RepathingActionSnapshot,
  RepathingControlSelection,
  RepathingGenerationResult,
  RepathingHeadingMode,
  RepathingPoint2d,
  RepathingState,
  RepathingTool,
} from './types';

const AUTH_ERROR_MESSAGE = 'Failed to authenticate. Please try again.';

type Deps = {
  global: typeof globalStores;
  parentStore: MotionEditorStore;
};

type RegenerateSceneObjectMotionPathStoreInput = Omit<
  RegenerateSceneObjectMotionPathInput,
  'apiKey' | 'constraint'
>;

function createInitialRepathingGenerationState() {
  return {
    status: 'idle',
    phase: null,
    activeRunId: null,
    error: null,
  } satisfies RepathingState['generation'];
}

function createInitialRepathingState(): RepathingState {
  return {
    actionStack: createInitialRepathingActionStackState(),
    controlsMode: 'body',
    tool: 'curve',
    headingMode: 'original',
    brushRadius: REPATHING_DEFAULT_BRUSH_RADIUS,
    brushStrength: REPATHING_DEFAULT_BRUSH_STRENGTH,
    curve: null,
    sourceCurve: null,
    curveSourceKey: null,
    hoveredControl: null,
    selectedControl: null,
    dragging: false,
    hasApiKey: false,
    generation: createInitialRepathingGenerationState(),
  };
}

class RepathingStore extends BaseStore<RepathingState, Deps> {
  private sceneID: string | null = null;

  constructor(deps: Deps) {
    super(createInitialRepathingState(), deps);
  }

  init() {
    this.syncApiKeyAvailability();
    this.syncControlsModeAccess();
    this.syncSceneScope();
    this.subscribeTo(
      this.deps.global.userStore.state,
      () => {
        this.syncApiKeyAvailability();
        this.syncControlsModeAccess();
      },
      { keys: ['apiKey', 'isCartwheeler'] },
    );
    this.subscribeTo(this.deps.parentStore.state, () => this.syncSceneScope(), {
      keys: 'scene',
    });
  }

  reset() {
    this.replaceStatePreservingMode(createInitialRepathingState());
    this.sceneID = this.deps.parentStore.state.scene.data?.sceneID ?? null;
    this.syncApiKeyAvailability();
  }

  setControlsMode(controlsMode: MotionEditControlsMode) {
    this.state.controlsMode =
      controlsMode === 'path' && !this.canUseRepathing ? 'body' : controlsMode;
    this.state.hoveredControl = null;
    this.state.selectedControl = null;
  }

  setTool(tool: RepathingTool) {
    this.state.tool = tool;
    this.state.hoveredControl = null;
    this.state.selectedControl = null;
  }

  setHeadingMode(headingMode: RepathingHeadingMode) {
    this.state.headingMode = headingMode;
  }

  setBrushRadius(radius: number) {
    this.state.brushRadius = clamp(
      radius,
      REPATHING_BRUSH_RADIUS_MIN,
      REPATHING_BRUSH_RADIUS_MAX,
    );
  }

  setBrushStrength(strength: number) {
    this.state.brushStrength = clamp(
      strength,
      REPATHING_BRUSH_STRENGTH_MIN,
      REPATHING_BRUSH_STRENGTH_MAX,
    );
  }

  ensureCurveFromBvh(bvh: BVH, sourceKey: string) {
    if (this.state.curveSourceKey === sourceKey && this.state.curve) {
      return true;
    }
    if (this.state.dragging) {
      return false;
    }

    const curve = createRepathingCurveFromBvh(bvh);
    if (!curve) {
      this.clearCurve();
      return false;
    }

    this.state.curve = cloneRepathingCurve(curve);
    this.state.sourceCurve = cloneRepathingCurve(curve);
    this.state.curveSourceKey = sourceKey;
    this.state.hoveredControl = null;
    this.state.selectedControl = null;
    this.state.actionStack = createInitialRepathingActionStackState();
    return true;
  }

  clearCurve() {
    this.state.curve = null;
    this.state.sourceCurve = null;
    this.state.curveSourceKey = null;
    this.state.hoveredControl = null;
    this.state.selectedControl = null;
    this.state.actionStack = createInitialRepathingActionStackState();
  }

  setHoveredControl(selection: RepathingControlSelection | null) {
    this.state.hoveredControl = selection ? { ...selection } : null;
  }

  setSelectedControl(selection: RepathingControlSelection | null) {
    this.state.selectedControl = selection ? { ...selection } : null;
  }

  setDragging(dragging: boolean) {
    this.state.dragging = dragging;
  }

  captureActionSnapshot(): RepathingActionSnapshot {
    return {
      curve: this.state.curve ? cloneRepathingCurve(this.state.curve) : null,
      selectedControl: this.state.selectedControl
        ? { ...this.state.selectedControl }
        : null,
    };
  }

  recordCurveEditAction({
    label,
    before,
  }: {
    label: string;
    before: RepathingActionSnapshot;
  }) {
    const after = this.captureActionSnapshot();
    if (areRepathingActionSnapshotsEqual(before, after)) {
      return false;
    }

    appendRepathingActionEntryInPlace(
      this.state.actionStack,
      createRepathingActionEntry({
        kind: 'curve-edit',
        label,
        before,
        after,
      }),
    );

    return true;
  }

  canUndoAction() {
    return canUndoStackAction(this.state.actionStack);
  }

  canRedoAction() {
    return canRedoStackAction(this.state.actionStack);
  }

  undoAction() {
    if (this.state.dragging || this.state.generation.status === 'pending') {
      return false;
    }
    if (!this.canUndoAction()) {
      return false;
    }

    const entry = this.state.actionStack.entries[this.state.actionStack.index];
    if (!entry) {
      return false;
    }

    this.applyActionSnapshot(entry.before);
    this.state.actionStack.index -= 1;
    return true;
  }

  redoAction() {
    if (this.state.dragging || this.state.generation.status === 'pending') {
      return false;
    }
    if (!this.canRedoAction()) {
      return false;
    }

    const nextIndex = this.state.actionStack.index + 1;
    const entry = this.state.actionStack.entries[nextIndex];
    if (!entry) {
      return false;
    }

    this.applyActionSnapshot(entry.after);
    this.state.actionStack.index = nextIndex;
    return true;
  }

  moveControl(
    selection: RepathingControlSelection,
    position: RepathingPoint2d,
  ) {
    const curve = this.state.curve;
    if (!curve) return;

    if (selection.handle === 'point') {
      moveRepathingPoint(curve, selection.pointId, position);
      return;
    }

    moveRepathingHandle(curve, selection.pointId, selection.handle, position);
  }

  applyBrush(center: RepathingPoint2d, delta: RepathingPoint2d) {
    if (!this.state.curve) return;

    applyRepathingBrush(
      this.state.curve,
      center,
      delta,
      this.state.brushRadius,
      this.state.brushStrength,
    );
  }

  smoothCurve() {
    if (!this.state.curve) return;

    const before = this.captureActionSnapshot();
    this.state.curve = smoothRepathingCurve(this.state.curve);
    this.state.hoveredControl = null;
    this.state.selectedControl = null;
    this.recordCurveEditAction({ label: 'Smooth path', before });
  }

  simplifyCurve() {
    if (!this.state.curve) return;

    const before = this.captureActionSnapshot();
    this.state.curve = simplifyRepathingCurve(this.state.curve);
    this.state.hoveredControl = null;
    this.state.selectedControl = null;
    this.recordCurveEditAction({ label: 'Simplify path', before });
  }

  async regenerateSceneObjectMotionPath(
    input: RegenerateSceneObjectMotionPathStoreInput,
  ): Promise<RepathingGenerationResult> {
    const apiKey = this.apiKey;
    if (!apiKey) {
      return { ok: false, error: AUTH_ERROR_MESSAGE };
    }
    if (!this.state.curve) {
      return { ok: false, error: 'Load a path before regenerating.' };
    }

    return regenerateSceneObjectMotionPath(this.getGenerationActionDeps(), {
      ...input,
      apiKey,
      constraint: buildRepathingRoot2dConstraint(
        this.state.curve,
        this.state.headingMode,
      ),
    });
  }

  private get apiKey() {
    return this.deps.global.userStore.state.apiKey.data?.apiKey ?? null;
  }

  private get canUseRepathing() {
    return canUseMotionEditorRepathing(
      this.deps.global.userStore.state.isCartwheeler,
    );
  }

  private getGenerationActionDeps() {
    return {
      parentStore: this.deps.parentStore,
      state: this.state,
    };
  }

  private applyActionSnapshot(snapshot: RepathingActionSnapshot) {
    const clonedSnapshot = cloneRepathingActionSnapshot(snapshot);
    this.state.curve = clonedSnapshot.curve;
    this.state.hoveredControl = null;
    this.state.selectedControl = clonedSnapshot.selectedControl;
  }

  private syncApiKeyAvailability() {
    this.state.hasApiKey = Boolean(this.apiKey);
  }

  private syncControlsModeAccess() {
    if (this.state.controlsMode === 'path' && !this.canUseRepathing) {
      this.state.controlsMode = 'body';
      this.state.hoveredControl = null;
      this.state.selectedControl = null;
      this.state.dragging = false;
    }
  }

  private syncSceneScope() {
    const sceneID = this.deps.parentStore.state.scene.data?.sceneID ?? null;
    if (this.sceneID === sceneID) {
      return;
    }

    const controlsMode = this.state.controlsMode;
    this.replaceStatePreservingMode(createInitialRepathingState());
    this.state.controlsMode = controlsMode;
    this.sceneID = sceneID;
    this.syncApiKeyAvailability();
  }

  private replaceStatePreservingMode(nextState: RepathingState) {
    this.state.controlsMode = nextState.controlsMode;
    this.state.tool = nextState.tool;
    this.state.headingMode = nextState.headingMode;
    this.state.brushRadius = nextState.brushRadius;
    this.state.brushStrength = nextState.brushStrength;
    this.state.curve = nextState.curve;
    this.state.sourceCurve = nextState.sourceCurve;
    this.state.curveSourceKey = nextState.curveSourceKey;
    this.state.hoveredControl = nextState.hoveredControl;
    this.state.selectedControl = nextState.selectedControl;
    this.state.dragging = nextState.dragging;
    this.state.actionStack = nextState.actionStack;
    this.state.hasApiKey = nextState.hasApiKey;
    this.state.generation = nextState.generation;
  }
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
}

export const { Provider, useStore, useStoreApi, Context } = createStoreProvider(
  {
    Store: RepathingStore,
    displayName: 'RepathingStore',
    parentStores: { parentStore: MotionEditorStoreContext },
    staticDeps: { global: globalStores },
  },
);
